import { motion } from "framer-motion";
import {
  Eye,
  ShieldCheck,
  Lightbulb,
  TrendingUp,
  MessageCircle,
  UserPlus,
} from "lucide-react";

/**
 * BrandValueFlywheel — reorders the 6 "why brand matters" points into a
 * cause-and-effect story arc, visualized as a connected path with numbered
 * nodes. On-brand palette (teal #0a3d54 + lime/cyan accents), no new fonts.
 *
 * Story arc:
 *   01 Recognition → 02 Trust → 03 Memory → 04 Value → 05 Referrals → 06 New Customers
 *
 * Desktop: horizontal 6-column path with an SVG dashed lime line connecting
 *          icon nodes across the top. Each node has number, icon ring, title,
 *          and one-sentence narrative below.
 * Mobile:  vertical left-rail timeline with icon nodes and text to the right.
 */
const STEPS = [
  {
    id: "recognition",
    title: "Recognition",
    icon: Eye,
    accent: { ring: "ring-cyan-400/40", text: "text-cyan-300", bg: "bg-cyan-500/15" },
    copy: "Every time someone sees your ad, your brand becomes more familiar.",
    handoff: "which leads to →",
  },
  {
    id: "trust",
    title: "Trust",
    icon: ShieldCheck,
    accent: { ring: "ring-emerald-400/40", text: "text-emerald-300", bg: "bg-emerald-500/15" },
    copy: "Familiarity breeds credibility. People buy from businesses they trust.",
    handoff: "which sticks as →",
  },
  {
    id: "memory",
    title: "Memory",
    icon: Lightbulb,
    accent: { ring: "ring-lime-400/50", text: "text-lime-300", bg: "bg-lime-400/15" },
    copy: "When someone needs what you offer, your name is the first that comes to mind.",
    handoff: "which compounds into →",
  },
  {
    id: "value",
    title: "Value",
    icon: TrendingUp,
    accent: { ring: "ring-amber-400/40", text: "text-amber-300", bg: "bg-amber-500/15" },
    copy: "Strong brands command premium prices. The more recognizable, the more valuable.",
    handoff: "which spreads as →",
  },
  {
    id: "referrals",
    title: "Referrals",
    icon: MessageCircle,
    accent: { ring: "ring-rose-400/40", text: "text-rose-300", bg: "bg-rose-500/15" },
    copy: "A memorable brand makes word-of-mouth possible: your most profitable source of growth.",
    handoff: "which returns as →",
  },
  {
    id: "new-customers",
    title: "New Customers",
    icon: UserPlus,
    accent: { ring: "ring-violet-400/40", text: "text-violet-300", bg: "bg-violet-500/15" },
    copy: "Today's viewer is tomorrow's buyer. The flywheel turns, and every impression counts.",
    handoff: null,
  },
];

export const BrandValueFlywheel = () => {
  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-cyan-300/25 bg-[#0a3d54]"
      data-testid="brand-value-flywheel"
    >
      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[480px] h-[280px] bg-[radial-gradient(ellipse_at_top_right,rgba(163,230,53,0.14),transparent_70%)] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[360px] h-[220px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(34,211,238,0.12),transparent_70%)] pointer-events-none" />

      <div className="relative p-6 md:p-10 lg:p-12">
        {/* Header */}
        <div className="mb-8 md:mb-12">
          <p className="text-lime-300 text-[11px] md:text-xs font-bold uppercase tracking-[0.3em] mb-2">
            How Brand Value Compounds
          </p>
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-3xl">
            Six steps, one flywheel. Each one feeds the next.
          </h3>
        </div>

        {/* Desktop: horizontal path */}
        <div className="hidden lg:block relative">
          {/* Dashed connector line across the top — behind nodes */}
          <svg
            aria-hidden
            className="absolute left-0 right-0 top-[32px] w-full h-[2px] pointer-events-none"
            preserveAspectRatio="none"
            viewBox="0 0 100 2"
          >
            <line
              x1="4" y1="1" x2="96" y2="1"
              stroke="rgba(163,230,53,0.45)"
              strokeWidth="0.5"
              strokeDasharray="1.5 1.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          <div className="grid grid-cols-6 gap-4 relative">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                  className="flex flex-col items-center text-center"
                  data-testid={`flywheel-step-${step.id}`}
                >
                  {/* Icon node */}
                  <div className={`relative w-16 h-16 rounded-full ${step.accent.bg} ring-2 ${step.accent.ring} ring-offset-4 ring-offset-[#0a3d54] flex items-center justify-center mb-4 bg-[#0a3d54]`}>
                    <Icon className={`w-6 h-6 ${step.accent.text}`} />
                  </div>
                  {/* Step number */}
                  <p className={`${step.accent.text} text-[11px] font-bold tracking-[0.25em] mb-1`}>
                    0{i + 1}
                  </p>
                  {/* Title */}
                  <h4 className="text-white text-lg font-bold mb-2">{step.title}</h4>
                  {/* Copy */}
                  <p className="text-slate-300 text-xs leading-relaxed px-1">
                    {step.copy}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Flywheel close-the-loop hint */}
          <div className="mt-10 pt-6 border-t border-cyan-300/15 text-center">
            <p className="text-cyan-200/70 text-xs uppercase tracking-[0.25em]">
              <span className="text-lime-300">↻</span>
              <span className="mx-2">New customers become recognition. And the cycle begins again</span>
              <span className="text-lime-300">↻</span>
            </p>
          </div>
        </div>

        {/* Mobile / tablet: vertical left-rail timeline */}
        <div className="lg:hidden relative">
          {/* Continuous rail line */}
          <div className="absolute left-[31px] top-4 bottom-4 w-px bg-gradient-to-b from-lime-400/50 via-cyan-400/30 to-violet-400/40 pointer-events-none" />

          <div className="space-y-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="relative flex items-start gap-4"
                  data-testid={`flywheel-step-${step.id}`}
                >
                  {/* Icon node */}
                  <div className={`relative flex-shrink-0 w-16 h-16 rounded-full ${step.accent.bg} ring-2 ${step.accent.ring} ring-offset-2 ring-offset-[#0a3d54] flex items-center justify-center z-10 bg-[#0a3d54]`}>
                    <Icon className={`w-6 h-6 ${step.accent.text}`} />
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className={`${step.accent.text} text-[11px] font-bold tracking-[0.25em]`}>
                        0{i + 1}
                      </span>
                      <h4 className="text-white text-lg font-bold">{step.title}</h4>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {step.copy}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Loop hint */}
          <div className="mt-8 pt-5 border-t border-cyan-300/15">
            <p className="text-cyan-200/70 text-xs uppercase tracking-[0.2em] text-center">
              <span className="text-lime-300">↻</span>
              <span className="mx-2">New customers become recognition: the flywheel turns</span>
              <span className="text-lime-300">↻</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
